import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const WEB_SESSION_SERVER_NAME = "web_session" as const;

// Sites with configured login-relay adapters on the sidecar. The list is
// short and explicit so the model never guesses: the fetch tool works for
// ANY url, but the login relay needs a server-side flow definition.
export const WEB_SESSION_CONFIGURED_SITES = ["ireps"] as const;

export const WEB_SESSION_ACTION_DESCRIPTION =
  "Browse the web with real browser sessions — including sites that require " +
  "logins. Works for any URL; for configured sites it keeps a persistent " +
  "authenticated session and can relay a login through the conversation " +
  "(captcha image + SMS OTP) when that session expires.";

export const WEB_SESSION_TOOLS_METADATA = [
  {
    name: "web_session_fetch",
    description:
      "Fetch any web page through a real browser (residential proxy, " +
      "JavaScript rendered) and get back its readable content — authenticated " +
      "where a stored login session exists for the page's domain. " +
      "Use this instead of plain fetch when a site blocks bots, needs " +
      "JavaScript, or needs a login. Pages whose content appears only after " +
      "an interaction accept `actions` (fill/click, in order, after load) — " +
      "e.g. IREPS tenders: fetch anonymSearch.do with a click on " +
      "input[value='Show Results']. A login_required outcome names the " +
      "configured site whose session expired — run web_session_login_start " +
      "for that site, complete the relay, and fetch again.",
    schema: {
      url: z
        .string()
        .url()
        .describe("The full URL to fetch (including protocol)"),
      screenshot: z
        .boolean()
        .optional()
        .describe(
          "Request a screenshot of the rendered page alongside the text " +
            "(default false — ask when layout or visuals matter)"
        ),
      actions: z
        .array(
          z.union([
            z.object({
              type: z.literal("fill"),
              selector: z
                .string()
                .max(300)
                .describe("CSS selector of the input"),
              value: z
                .string()
                .max(300)
                .describe("Text to type into the input"),
            }),
            z.object({
              type: z.literal("click"),
              selector: z
                .string()
                .max(300)
                .describe("CSS selector of the button/control to click"),
            }),
          ])
        )
        .max(8)
        .optional()
        .describe(
          "Optional form interactions applied to the loaded page, in order, " +
            "before the content is read (for pages whose data appears only " +
            "after a fill/click)"
        ),
    },
    stake: "never_ask",
    enableAlerting: true,
    displayLabels: {
      running: "Fetching page (browser session)",
      done: "Fetched page",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
  {
    name: "web_session_status",
    description:
      "Report the stored login-session state for a configured site: none, " +
      "awaiting_captcha, awaiting_otp, logged_in, login_failed, or expired.",
    schema: {
      site: z
        .enum(WEB_SESSION_CONFIGURED_SITES)
        .describe("The configured site to check"),
    },
    stake: "never_ask",
    enableAlerting: true,
    displayLabels: {
      running: "Checking browser session",
      done: "Browser session check",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
  {
    name: "web_session_login_start",
    description:
      "Start the login relay for a configured site: opens the login page " +
      "with the user's registered mobile number and returns a captcha image " +
      "to show the user. Ask the USER for their registered mobile number — " +
      "never invent or reuse one from memory. No SMS/OTP is sent until the " +
      "captcha is accepted, so a failed attempt costs nothing.",
    schema: {
      site: z
        .enum(WEB_SESSION_CONFIGURED_SITES)
        .describe("The configured site to log into"),
      mobile: z
        .string()
        .regex(/^\d{8,15}$/)
        .describe(
          "The user's registered mobile number for the site (8-15 digits). " +
            "Ask the user for it if they have not provided it — never invent one"
        ),
      solve: z
        .enum(["auto", "human"])
        .optional()
        .describe(
          "Captcha handling: 'auto' (default) lets the sidecar solve the " +
            "captcha itself when it can and go straight to the OTP step; " +
            "'human' always returns the captcha image for the user to read " +
            "and type. Use 'human' when the user wants to enter the code " +
            "themselves"
        ),
    },
    stake: "low",
    enableAlerting: true,
    displayLabels: {
      running: "Starting login relay",
      done: "Login captcha ready",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
  {
    name: "web_session_login_submit_captcha",
    description:
      "Submit the captcha code the user read from the " +
      "web_session_login_start image. On acceptance the sidecar requests the " +
      "OTP and the user's phone receives the SMS (most sites cap OTP " +
      "requests per hour — never retry this blindly). Collect the OTP from " +
      "the user and pass it to web_session_login_submit_otp.",
    schema: {
      site: z
        .enum(WEB_SESSION_CONFIGURED_SITES)
        .describe("The configured site the login relay is running for"),
      captchaText: z
        .string()
        .min(1)
        .describe("The captcha code exactly as the user read it"),
    },
    stake: "low",
    enableAlerting: true,
    displayLabels: {
      running: "Submitting login captcha",
      done: "Login captcha submitted",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
  {
    name: "web_session_login_submit_otp",
    description:
      "Submit the SMS OTP the user received on their registered mobile. On " +
      "success the site's session is stored and web_session_fetch works " +
      "authenticated for that domain until the session expires.",
    schema: {
      site: z
        .enum(WEB_SESSION_CONFIGURED_SITES)
        .describe("The configured site the login relay is running for"),
      otp: z
        .string()
        .regex(/^\d{4,6}$/)
        .describe("The numeric OTP from the user's SMS"),
    },
    stake: "low",
    enableAlerting: true,
    displayLabels: {
      running: "Submitting login OTP",
      done: "Login OTP submitted",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
] as const;

export const WEB_SESSION_SERVER = {
  serverInfo: {
    name: WEB_SESSION_SERVER_NAME,
    version: "1.0.0",
    description: WEB_SESSION_ACTION_DESCRIPTION,
    authorization: null,
    icon: "ActionGlobeAltIcon" as const,
    documentationUrl: null,
  },
  tools: WEB_SESSION_TOOLS_METADATA,
} as const satisfies ServerMetadata;
